/* eslint-disable import/no-relative-packages */
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { BLOCKS, Document } from '@contentful/rich-text-types'
import { useLocation } from '@reach/router'
import { slugify } from '../../src/utils/strings'

export const locales = [
  {
    slug: 'en-us',
    contentfulCode: 'en-US',
    label: 'English',
  },
  {
    slug: 'de-de',
    contentfulCode: 'de-DE',
    label: 'Deutsch',
  },
  {
    slug: 'es-la',
    contentfulCode: 'es-419',
    label: 'Español',
  },
  {
    slug: 'fr-fr',
    contentfulCode: 'fr-FR',
    label: 'Français',
  },
  {
    slug: 'ja-jp',
    contentfulCode: 'ja-JP',
    label: '日本語',
  },
  {
    slug: 'nl-nl',
    contentfulCode: 'nl-NL',
    label: 'Nederlands',
  },
]

export const defaultLocale = locales[0].slug

// transforms `/ja-jp/products/` => [`/ja-jp/products/`, `ja-jp`, `/products/`]
const pathLocaleRegex = /^\/([a-zA-Z]{2}-[a-zA-Z]{2})(\/.*)$/

// based on current path, return locale info
export const useLocale = () => {
  const { pathname } = useLocation()
  const currentLocale = getLocale(pathname)
  const currentContentfulCode =
    locales.find((loc) => loc.slug === currentLocale)?.contentfulCode || locales[0].contentfulCode
  return {
    currentLocale,
    currentContentfulCode,
    lang: currentLocale.split('-')[0],
    region: currentLocale.split('-')[1],
  }
}

export const getLocale = (path: string) => {
  const matches = path?.match(pathLocaleRegex)
  return matches && matches.length === 3 ? matches[1] : defaultLocale
}

export const getBrowserLocaleBestMatch = (fallback = defaultLocale) => {
  if (typeof window === 'undefined') return useLocale().currentLocale
  let { language } = navigator
  if (language.length > 2) language = language.substring(0, 2)
  language = language.toLowerCase()
  const matchedLocale = locales.find((local) => local.slug.substring(0, 2) === language)
  if (matchedLocale) return matchedLocale.slug
  return fallback
}

export const localizedPath = (path: string, locale: string) => {
  let newPath = path

  // sanitize input
  if (!newPath || typeof newPath !== 'string' || newPath.trim().length < 1) return ''

  // check for unnecessary https://bitwarden.com prefix
  if (
    newPath.toLowerCase().includes('www.bitwarden.com') ||
    (newPath.toLowerCase().includes('bitwarden.com') && !newPath.toLowerCase().includes('.bitwarden.com'))
  ) {
    newPath = path.substring(path.toLowerCase().indexOf('bitwarden.com') + 13)
    if (!newPath.startsWith('/')) newPath = `/${newPath}`
  }

  if (
    newPath.toLowerCase().startsWith('https://vault.bitwarden.com/') &&
    (newPath.toLocaleLowerCase().includes('register') ||
      newPath.toLocaleLowerCase().includes('login') ||
      newPath.toLocaleLowerCase().includes('trial'))
  ) {
    return ['de-de', 'fr-fr', 'nl-nl'].includes(locale)
      ? `https://vault.bitwarden.eu/${newPath.substring(28)}`
      : newPath
  }

  // check if this is an absolute path, ex: https://google.com
  if (!newPath.startsWith('/') || newPath.startsWith('//')) return path

  // make sure the path ends with a trailing slash
  if (!newPath.includes('#') && !newPath.includes('?') && !newPath.endsWith('/') && !newPath.includes('.'))
    newPath += '/'
  else if (newPath.includes('?') && !newPath.includes('/?')) newPath = newPath.replace('?', '/?')
  else if (newPath.includes('#') && !newPath.includes('/#') && !newPath.includes('?'))
    newPath = newPath.replace('#', '/#')

  // check if this is a valid locale slug
  const localeSlug =
    locales.find((loc) => locale === loc.slug)?.slug ||
    locales.find((loc) => locale === loc.contentfulCode)?.slug ||
    locales.find((loc) => locale.localeCompare(loc.slug, defaultLocale, { sensitivity: 'base' }) === 0)?.slug ||
    locales.find((loc) => locale.localeCompare(loc.contentfulCode, defaultLocale, { sensitivity: 'base' }) === 0)
      ?.slug ||
    defaultLocale

  // if the path contains one of the following slugs, replace it with the locale slug
  // inactive for now
  const sourceLocale = getLocale(path)
  if (!sourceLocale) {
    const slugMap = {
      'en-us': {
        blog: '/blog/',
        help: '/help/',
        learning: '/learning/',
        resources: '/resources/',
        search: '/search/',
      },
      'de-de': {
        blog: '/blog/',
        help: '/hilfe/',
        learning: '/lernen/',
        resources: '/ressourcen/',
        search: '/suche/',
      },
      'es-la': {
        blog: '/blog/',
        help: '/ayuda/',
        learning: '/aprendizaje/',
        resources: '/recursos/',
        search: '/buscar/',
      },
      'fr-fr': {
        blog: '/blog/',
        help: '/aide/',
        learning: '/apprendre/',
        resources: '/ressources/',
        search: '/rechercher/',
      },
      'ja-jp': {
        blog: '/ブログ/',
        help: '/ヘルプ/',
        learning: '/学ぶ/',
        resources: '/リソース/',
        search: '/検索/',
      },
      'nl-nl': {
        blog: '/blog/',
        help: '/help/',
        learning: '/leren/',
        resources: '/resources/',
        search: '/search/',
      },
    }

    Object.keys(slugMap[sourceLocale]).forEach((key) => {
      newPath = newPath.replace(slugMap[sourceLocale][key], slugMap[localeSlug][key])
    })
  }

  // strip a possible locale prefix
  const matches = newPath.match(pathLocaleRegex)
  if (matches && matches.length === 3) [, , newPath] = matches
  return localeSlug === defaultLocale ? newPath : `/${localeSlug}${newPath}`
}

export const getAlgoliaIndexName = (locale: string) => {
  let env = process.env.GATSBY_ALGOLIA_ENV
  if (!env) env = !process.env.BRANCH || process.env.BRANCH === 'main' ? 'prod' : 'dev'
  return `${env}_bitwarden_${(locale || defaultLocale).replace('-', '_').toLocaleLowerCase()}`
}

export const algoliaQueries = locales
  .map((locale) => locale.contentfulCode)
  .map((locale) => [
    {
      indexName: getAlgoliaIndexName(locale),
      queryVariables: { locale },
      query: `query AllContentfulSeo($locale: String!) {
      allContentfulSeo(filter: {node_locale: { eq: $locale }, title: {ne: null}, path: {ne: null}, noIndex: {ne: true}}) {
          nodes {
            contentful_id
            title
            description {
              description
            }
            path
            updatedAt
            keywords
            node_locale
            internal {
              contentDigest
            }
          }
        }
      }`,
      transformer: ({ data }) =>
        data.allContentfulSeo.nodes.map((node: Queries.ContentfulSeo) => ({
          id: node.contentful_id,
          type: 'pages',
          url: localizedPath(node.path, node.node_locale),
          updatedAt: node.updatedAt,
          title: node.title,
          body: node.description?.description || '',
          keywords: node.keywords,
          internal: node.internal,
        })),
      matchFields: ['url', 'updatedAt'],
    },
    {
      indexName: getAlgoliaIndexName(locale),
      queryVariables: { locale },
      query: `query AllContentfulLandingPage($locale: String!) {
        allContentfulLandingPage(filter: {node_locale: {eq: $locale}, seo: {noIndex: {eq: false}, path: {eq: null}}}) {
          nodes {
            contentful_id
            slug
            node_locale
            seo {
              title
              description {
                description
              }
              keywords
              updatedAt
            }
            internal {
              contentDigest
            }
          }
        }
      }`,
      transformer: ({ data }) =>
        data.allContentfulLandingPage.nodes.map((node: Queries.ContentfulLandingPage) => ({
          id: node.contentful_id,
          type: 'pages',
          url: localizedPath(`/${node.slug}/`, node.node_locale),
          updatedAt: node.seo.updatedAt,
          title: node.seo.title,
          body: node.seo.description?.description || '',
          keywords: node.seo.keywords,
          internal: node.internal,
        })),
      matchFields: ['url', 'updatedAt'],
    },
    {
      indexName: getAlgoliaIndexName(locale),
      queryVariables: { locale },
      query: `query AllContentfulHelpArticle($locale: String!) {
      allContentfulHelpArticle(filter: {node_locale: { eq: $locale }, title: {ne: null}, seo: {noIndex: {ne: true}}}) {
        nodes {
          contentful_id
          slug
          title
          updatedAt
          node_locale
          body {
            raw
            references {
              ... on ContentfulComponentCallout {
                __typename
                contentful_id
                type
                body {
                  raw
                }
              }
            }
          }
          seo {
            updatedAt
            description {
              description
            }
            keywords
          }
          internal {
            contentDigest
          }
        }
      }
    }`,
      transformer: ({ data }) =>
        data.allContentfulHelpArticle.nodes
          .map((node: Queries.ContentfulHelpArticle) => {
            const updatedAt = (node.seo?.updatedAt || 0) > (node.updatedAt || 0) ? node.seo?.updatedAt : node.updatedAt
            const url = localizedPath(`/help/${node.slug}/`, node.node_locale)

            const records = [
              {
                id: node.contentful_id,
                type: 'help',
                url,
                hash: null,
                updatedAt,
                title: node.title,
                body: node.seo?.description?.description || '',
                keywords: node.seo?.keywords,
                internal: node.internal,
              },
            ]

            let title = ''
            let body = ''
            if (node.body?.raw) {
              const document: Document = JSON.parse(node.body.raw)
              const titles = [BLOCKS.HEADING_2, BLOCKS.HEADING_3]
              const bodies = [BLOCKS.PARAGRAPH, BLOCKS.UL_LIST, BLOCKS.OL_LIST, BLOCKS.QUOTE]

              document.content.forEach((block) => {
                if (bodies.includes(block.nodeType)) {
                  const text = documentToPlainTextString(block).trim()
                  if (text?.length > 0) body += `${text}\n`
                } else if (block.nodeType === BLOCKS.EMBEDDED_ENTRY) {
                  const ref = node.body.references?.find(
                    (obj) => obj.contentful_id === block.data.target?.sys?.id
                  ) as Queries.ContentfulComponentCallout
                  if (ref) {
                    body += `${
                      { warning: 'Warning', info: 'Note', success: 'Tip' }[ref.type]
                    }: ${documentToPlainTextString(JSON.parse(ref.body.raw))}\n`
                  }
                } else if (titles.includes(block.nodeType)) {
                  if (title === '') records[0].body += `\n${body.trim()}`
                  else {
                    records.push({
                      id: `${node.contentful_id}#${slugify(title)}`,
                      type: 'help',
                      url,
                      hash: slugify(title),
                      updatedAt,
                      title,
                      body: body.trim(),
                      keywords: [],
                      internal: node.internal,
                    })
                  }
                  title = documentToPlainTextString(block)
                  body = ''
                }
              })
            }
            if (title !== '')
              records.push({
                id: `${node.contentful_id}#${slugify(title)}`,
                type: 'help',
                url,
                hash: slugify(title),
                updatedAt,
                title,
                body: body.trim(),
                keywords: [],
                internal: node.internal,
              })
            return records
          })
          .flat(),

      matchFields: ['url', 'updatedAt'],
    },
    {
      indexName: getAlgoliaIndexName(locale),
      queryVariables: { locale },
      query: `query AllContentfulLearningPage($locale: String!) {
        allContentfulLearningPage(
          filter: {node_locale: {eq: $locale}, title: {ne: null}, seo: {noIndex: {ne: true}}}
        ) {
          nodes {
            contentful_id
            slug
            title
            updatedAt
            node_locale
            sections {
              ... on ContentfulComponentRichText {
                body {
                  raw
                }
              }
            }
            seo {
              updatedAt
              description {
                description
              }
              keywords
            }
            internal {
              contentDigest
            }
          }
        }
      }`,
      transformer: ({ data }) =>
        data.allContentfulLearningPage.nodes.map((node: Queries.ContentfulLearningPage) => ({
          id: node.contentful_id,
          type: 'learning',
          url: localizedPath(`/learning/${node.slug}/`, node.node_locale),
          hash: null,
          updatedAt: (node.seo?.updatedAt || 0) > (node.updatedAt || 0) ? node.seo?.updatedAt : node.updatedAt,
          title: node.title,
          body: documentToPlainTextString(
            JSON.parse(
              (node.sections as unknown as Queries.ContentfulComponentRichText[])
                .filter((section) => section.body)
                .reduce((acc, section) => ({ ...acc, ...section.body }), { raw: '' })?.raw
            )
          ),
          keywords: node.seo?.keywords,
          internal: node.internal,
        })),
      matchFields: ['url', 'updatedAt'],
    },
    {
      indexName: getAlgoliaIndexName(locale),
      queryVariables: { locale },
      query: `query AllContentfulBlogPage ($locale: String!) {
      allContentfulBlogPage(filter: {node_locale: { eq: $locale }, title: {ne: null}}) {
        nodes {
          id
          slug
          updatedAt
          title
          node_locale
          description
          internal {
            contentDigest
          }
        }
      }
    }`,
      transformer: ({ data }) =>
        data.allContentfulBlogPage.nodes.map((node: Queries.ContentfulBlogPage) => ({
          id: node.id,
          type: 'blog',
          url: localizedPath(`/blog/${node.slug}/`, node.node_locale),
          updatedAt: node.updatedAt,
          title: node.title,
          body: node.description,
          keywords: [],
          internal: node.internal,
        })),
      matchFields: ['url', 'updatedAt'],
    },
    {
      indexName: getAlgoliaIndexName(locale),
      queryVariables: { locale },
      query: `query AllContentfulResourcePage ($locale: String!) {
      allContentfulResourcePage(filter: {node_locale: { eq: $locale }, seo: { noIndex: { ne: true } } }) {
        nodes {
          contentful_id
          title
          description
          slug
          updatedAt
          node_locale
          seo {
            updatedAt
            keywords
          }
          internal {
            contentDigest
          }
        }
      }
    }`,
      transformer: ({ data }) =>
        data.allContentfulResourcePage.nodes.map((node: Queries.ContentfulResourcePage) => ({
          id: node.contentful_id,
          type: 'resources',
          url: localizedPath(`/resources/${node.slug}/`, node.node_locale),
          updatedAt: (node.seo?.updatedAt || 0) > (node.updatedAt || 0) ? node.seo?.updatedAt : node.updatedAt,
          title: node.title,
          body: node.description || '',
          keywords: node.seo?.keywords,
          internal: node.internal,
        })),
      matchFields: ['url', 'updatedAt'],
    },
    {
      indexName: getAlgoliaIndexName(locale),
      queryVariables: { locale },
      query: `query AllContentfulGooglePresentation($locale: String!) {
        allContentfulGooglePresentation(filter: {node_locale: {eq: $locale}}) {
          nodes {
            contentful_id
            title
            slug
            updatedAt
            node_locale
            url
            speakerNotes {
              raw
            }
            internal {
              contentDigest
            }
          }
        }
      }`,
      transformer: ({ data }) =>
        data.allContentfulGooglePresentation.nodes.map((node: Queries.ContentfulGooglePresentation) => ({
          id: node.contentful_id,
          type: 'resources',
          url: node.url?.includes('docs.google.com/presentation')
            ? localizedPath(`/resources/presentations/${node.slug}/`, node.node_locale)
            : node.url,
          updatedAt: node.updatedAt,
          title: node.title,
          body: node.speakerNotes?.raw ? documentToPlainTextString(JSON.parse(node.speakerNotes.raw)) : '',
          internal: node.internal,
        })),
      matchFields: ['url', 'updatedAt'],
    },
    {
      indexName: getAlgoliaIndexName(locale),
      queryVariables: { locale },
      query: `query AllContentfulEvents($locale: String!) {
        allContentfulEvents(filter: {node_locale: {eq: $locale}}) {
          nodes {
            contentful_id
            slug
            updatedAt
            tags
            seo {
              title
              description {
                description
              }
            }
            internal {
              contentDigest
            }
          }
        }
      }`,
      transformer: ({ data }) =>
        data.allContentfulEvents.nodes.map((node: Queries.ContentfulEvents) => ({
          id: node.contentful_id,
          type: 'events',
          url: `/events/${node.slug}/`,
          updatedAt: node.updatedAt,
          title: node.seo?.title,
          body: node.seo?.description?.description || '',
          keywords: node.tags,
          internal: node.internal,
        })),
      matchFields: ['url', 'updatedAt'],
    },
    {
      indexName: getAlgoliaIndexName(locale),
      queryVariables: { locale },
      query: `query AllContentfulNewsfeed($locale: String!) {
        allContentfulNewsfeed(filter: {node_locale: {eq: $locale}}) {
          nodes {
            contentful_id
            title
            description {
              description
            }
            url
            updatedAt
            tags
            internal {
              contentDigest
            }
          }
        }
      }`,
      transformer: ({ data }) =>
        data.allContentfulNewsfeed.nodes.map((node: Queries.ContentfulNewsfeed) => ({
          id: node.contentful_id,
          type: 'newsfeed',
          url: node.url,
          updatedAt: node.updatedAt,
          title: node.title,
          body: node?.description?.description || '',
          keywords: node.tags,
          internal: node.internal,
        })),
      matchFields: ['url', 'updatedAt'],
    },
    {
      indexName: getAlgoliaIndexName(locale),
      queryVariables: { locale },
      query: `query AllContentfulComponentFaq ($locale: String!) {
        allContentfulComponentFaq(filter: {node_locale: {eq: $locale}, answer: {raw: {ne: null}}, question: {ne: null}}) {
          nodes {
            contentful_id
            question
            answer {
              raw
            }
            category
            updatedAt
            internal {
              contentDigest
            }
          }
        }
      }`,
      transformer: ({ data }) =>
        data.allContentfulComponentFaq.nodes.map((node: Queries.ContentfulComponentFaq) => ({
          id: node.contentful_id,
          type: 'tips',
          url: `/tips/#${slugify(node.question)}`,
          updatedAt: node.updatedAt,
          title: node.question,
          body: node.answer?.raw ? documentToPlainTextString(JSON.parse(node.answer.raw)) : '',
          keywords: [node.category],
          internal: node.internal,
        })),
      matchFields: ['url', 'updatedAt'],
    },
    {
      indexName: `${getAlgoliaIndexName(locale)}_query_suggestions_external_analytics`,
      queryVariables: { locale },
      query: `query AllContentfulComponentFaq ($locale: String!) {
        allContentfulComponentFaq(filter: { node_locale: {eq: $locale}}) {
          nodes {
            contentful_id
            question
            updatedAt
            internal {
              contentDigest
            }
          }
        }
      }`,
      transformer: ({ data }) =>
        data.allContentfulComponentFaq.nodes.map((node: Queries.ContentfulComponentFaq) => ({
          id: node.contentful_id,
          query: node.question.replace(/[^\w\s]/gi, ''), // algolia only accepts alpha numeric chars https://www.algolia.com/doc/guides/building-search-ui/ui-and-ux-patterns/query-suggestions/js/#suggestion-normalization-and-completion
          count: 50,
          internal: node.internal,
        })),
      matchFields: ['query'],
    },
  ])
  .flat()
